import React, {useContext, useEffect, useState} from 'react';
import {Button, TableCell} from '@bosch/react-frok';
import {ObjectType} from '../../../models/ObjectType';
import {StorageObject} from '../../../models/StorageObject';
import {ItemThumbPlaceholder} from '../../ItemPlaceholder/ItemThumbPlaceholder';
import {
    executeAction,
    formatDate,
    formatFiletype,
    formatSize,
    getFileExtension,
    isPreviewApplicable
} from '../../../utils';
import {t} from 'i18next';
import {AuthContext} from '../../../context/AuthContext';
import {hasEditorialAccess, hasPublicOnlyAccess} from '../../../utils/UserUtils';
import {LabelDTO} from '../../../models/dto/LabelDTO';
import {Label} from '../../../models/Label';
import {getLabel} from '../../../utils/StorageObjectUtils';
import {ActionsCell, CommandActionWrapper, ItemEntryRow, Title, TitleCell} from './ItemTableRow.style';
import {LayoutViewType} from '../../../models/LayoutViewType';
import {isFileDownloadable} from '../../../utils/DtoUtils';
import {generateIdentifier} from '../../../utils/IdentityUtils';

export interface ItemProps {
    readonly item: StorageObject;
    readonly language: string;
    readonly defaultLocale: string;
    readonly showEdit: boolean;
    readonly isFavoriteToggle: boolean | null;
    readonly labelData: LabelDTO[];
    onDeleteClicked: () => void;
    onDownloadClicked: () => void;
    onEditClicked: () => void;
    onDetailsClicked: () => void;
    onFavoriteClicked: () => void;
    onPreviewClicked: () => void;
    onCopyUrlClicked: () => void;
    onClick: () => void;
}

/**
 * @name ItemTableRow
 * @description
 * Row representation of an item (folder/file) for the ItemTable component
 */
export const ItemTableRow = (props: ItemProps) => {
    const {isAuthenticated, roles} = useContext(AuthContext);
    const [isPublicOnlyAccess] = useState<boolean>(hasPublicOnlyAccess(isAuthenticated, roles));
    const [isEditorialAccess] = useState<boolean>(hasEditorialAccess(isAuthenticated, roles));
    const [label, setLabel] = useState<Label>(getLabel(props.defaultLocale, props.item.labels));

    useEffect(() => {
        setLabel(getLabel(props.defaultLocale, props.item.labels));
    }, [props.defaultLocale, props.item.labels]);

    const commandActions = () => {
        return (
            <CommandActionWrapper id={generateIdentifier()}>
                {props.showEdit && (
                    <div id={generateIdentifier()}>
                        <Button id={generateIdentifier()}
                                key='edit'
                                icon='edit'
                                mode='tertiary'
                                type='button'
                                title={t('GENERAL_EDIT_LABEL')}
                                onClick={(e) => executeAction(e, props.onEditClicked)}/>
                        <Button id={generateIdentifier()}
                                key='delete'
                                icon='delete'
                                mode='tertiary'
                                type='button'
                                title={t('GENERAL_DELETE_LABEL')}
                                onClick={(e) => executeAction(e, props.onDeleteClicked)}/>
                    </div>
                )}
                <Button id={generateIdentifier()}
                        key='view'
                        icon='document'
                        mode='tertiary'
                        type='button'
                        title={t('GENERAL_VIEW_LABEL')}
                        onClick={(e) => executeAction(e, props.onDetailsClicked)}/>
                {(!isPublicOnlyAccess) &&
                <Button id={generateIdentifier()}
                        key='favorites'
                        icon={!!props.isFavoriteToggle ? 'wishlist-filled' : 'wishlist'}
                        mode='tertiary'
                        type='button'
                        title={t('GENERAL_FAVORITES_LABEL')}
                        onClick={(e) => executeAction(e, props.onFavoriteClicked)}/>
                }
                {(isPreviewApplicable(props.item.metadata.contentType, getFileExtension(props.item.metadata.blobUrl))) &&
                <Button id={generateIdentifier()}
                        key='preview'
                        icon='watch-on'
                        mode='tertiary'
                        type='button'
                        title={t('GENERAL_PREVIEW_LABEL')}
                        onClick={(e) => executeAction(e, props.onPreviewClicked)}/>
                }
                {(props.item.objectType === ObjectType.FILE) && (isFileDownloadable(props.item.status)) && (
                    <Button id={generateIdentifier()}
                            key='download'
                            icon='download'
                            mode='tertiary'
                            type='button'
                            title={t('GENERAL_DOWNLOAD_LABEL')}
                            onClick={(e) => executeAction(e, props.onDownloadClicked)}/>
                )}
                <Button id={generateIdentifier()}
                        key='copy-button'
                        icon='copy'
                        mode='tertiary'
                        type='button'
                        title={t('GENERAL_COPY_URL_LABEL')}
                        onClick={(e) => executeAction(e, props.onCopyUrlClicked)}/>
            </CommandActionWrapper>
        )
    };

    return (
        <ItemEntryRow id={generateIdentifier()}
                      onClick={(e) => executeAction(e, props.onClick)}>
            <TableCell id={generateIdentifier()}>
                <ItemThumbPlaceholder
                    contentType={(props.item.objectType === ObjectType.FOLDER) ? 'folder' : props.item.metadata.contentType ?? null}
                    viewType={LayoutViewType.TABLE}/>
            </TableCell>
            <TitleCell id={generateIdentifier()}>
                <Title id={generateIdentifier()} className='highlight'>
                    {label.name}
                </Title>
            </TitleCell>
            <TableCell id={generateIdentifier()}>
                {formatFiletype(props.item.objectType === ObjectType.FOLDER ? 'folder' : props.item.metadata.contentType ?? null)}
            </TableCell>
            <TableCell id={generateIdentifier()}>
                {formatDate(props.language, props.item.metadata.dateCreated)}
            </TableCell>
            <TableCell id={generateIdentifier()} align='right'>
                {formatSize(props.item.totalSize)}
            </TableCell>
            {isEditorialAccess ?
                <TableCell id={generateIdentifier()}>
                    {props.item.isPublic ? t('PUBLIC_ACCESS_TRUE_VALUE') : t('PUBLIC_ACCESS_FALSE_VALUE')}
                </TableCell>
                : <div id={generateIdentifier()}></div>
            }
            <ActionsCell id={generateIdentifier()}>
                {commandActions()}
            </ActionsCell>
        </ItemEntryRow>
    );
};
